import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from 'axios';

export function safeParse(value, key) {
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch (err) {
        if (key) {
            localStorage.removeItem(key);
        }
        console.error('Failed to parse JSON', err);
        return null;
    }
}

export async function fetchData(url, config) {
    try {
        if (config) {
            if (config.headers || config.signal) {
                const response = await axios.get(url, config);
                return response.data;
            } else {
                const response = await axios.get(url, { headers: config });
                return response.data;
            }
        } else {
            const response = await axios.get(url);
            return response.data;
        }
    } catch (error) {
        console.error('GET request failed:', error);
        throw error;
    }
}

// Simple ROT13 encoder/decoder
export function rot13(str) {
    return str.replace(/[a-zA-Z]/g, function(c) {
        return String.fromCharCode(
            (c <= 'Z' ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26
        );
    });
}

// Save the user's email encoded with ROT13
export function saveEmailToLocalStorage(email) {
    try {
        localStorage.setItem('userEmail', rot13(email));
    } catch (err) {
        console.error('Failed to store email', err);
    }
}

// Retrieve and decode the stored email
export function getEmailFromLocalStorage() {
    try {
        const encoded = localStorage.getItem('userEmail');
        return encoded ? rot13(encoded) : null;
    } catch (err) {
        console.error('Failed to retrieve email', err);
        return null;
    }
}

/**
 * Makes a POST request using axios.
 * @param {string} url - The endpoint URL.
 * @param {object} data - The request body data.
 * @param {object} [config={}] - Optional Axios config (e.g. headers).
 * @returns {Promise<object>} - The response data.
 */
export async function postData(url, data, config = {}) {
    try {
        // Attach email for log-error endpoint if not already provided
        if (url.includes('/api/log-error')) {
            const email = data && data.email ? data.email : getEmailFromLocalStorage();
            if (email) {
                data = { ...data, email };
            }
        }

        const response = await axios.post(url, data, config);
        return response.data;
    } catch (error) {
        if (error.code === 'ECONNABORTED') {
            console.error('Request timed out');
        } else {
            console.error('Something else went wrong', error);
            throw error;
        }
    }
}

export const getMultiples = (f, t) => 
   [...(Array(Math.floor(t / f)))]
   .map((_, i) => f * (i + 1));

const allCountries = [
	"Afghanistan",
	"Albania",
	"Algeria",
	"American Samoa",
	"Andorra",
	"Angola",
	"Anguilla",
	"Antarctica",
	"Antigua and Barbuda",
	"Argentina",
	"Armenia",
	"Aruba",
	"Australia",
	"Austria",
	"Azerbaijan",
	"Bahamas (the)",
	"Bahrain",
	"Bangladesh",
	"Barbados",
	"Belarus",
	"Belgium",
	"Belize",
	"Benin",
	"Bermuda",
	"Bhutan",
	"Bolivia (Plurinational State of)",
	"Bonaire, Sint Eustatius and Saba",
	"Bosnia and Herzegovina",
	"Botswana",
	"Bouvet Island",
	"Brazil",
	"British Indian Ocean Territory (the)",
	"Brunei Darussalam",
	"Bulgaria",
	"Burkina Faso",
	"Burundi",
	"Cabo Verde",
	"Cambodia",
	"Cameroon",
	"Canada",
	"Cayman Islands (the)",
	"Central African Republic (the)",
	"Chad",
	"Chile",
	"China",
	"Christmas Island",
	"Cocos (Keeling) Islands (the)",
	"Colombia",
	"Comoros (the)",
	"Congo (the Democratic Republic of the)",
	"Congo (the)",
	"Cook Islands (the)",
	"Costa Rica",
	"Croatia",
	"Cuba",
	"Curaçao",
	"Cyprus",
	"Czechia",
	"Côte d'Ivoire",
	"Denmark",
	"Djibouti",
	"Dominica",
	"Dominican Republic (the)",
	"Ecuador",
	"Egypt",
	"El Salvador",
	"Equatorial Guinea",
	"Eritrea",
	"Estonia",
	"Eswatini",
	"Ethiopia",
	"Falkland Islands (the) [Malvinas]",
	"Faroe Islands (the)",
	"Fiji",
	"Finland",
	"France",
	"French Guiana",
	"French Polynesia",
	"French Southern Territories (the)",
	"Gabon",
	"Gambia (the)",
	"Georgia",
	"Germany",
	"Ghana",
	"Gibraltar",
	"Greece",
	"Greenland",
	"Grenada",
	"Guadeloupe",
	"Guam",
	"Guatemala",
	"Guernsey",
	"Guinea",
	"Guinea-Bissau",
	"Guyana",
	"Haiti",
	"Heard Island and McDonald Islands",
	"Holy See (the)",
	"Honduras",
	"Hong Kong",
	"Hungary",
	"Iceland",
	"India",
	"Indonesia",
	"Iran (Islamic Republic of)",
	"Iraq",
	"Ireland",
	"Isle of Man",
	"Israel",
	"Italy",
	"Jamaica",
	"Japan",
	"Jersey",
	"Jordan",
	"Kazakhstan",
	"Kenya",
	"Kiribati",
	"Korea (the Democratic People's Republic of)",
	"Korea (the Republic of)",
	"Kuwait",
	"Kyrgyzstan",
	"Lao People's Democratic Republic (the)",
	"Latvia",
	"Lebanon",
	"Lesotho",
	"Liberia",
	"Libya",
	"Liechtenstein",
	"Lithuania",
	"Luxembourg",
	"Macao",
	"Madagascar",
	"Malawi",
	"Malaysia",
	"Maldives",
	"Mali",
	"Malta",
	"Marshall Islands (the)",
	"Martinique",
	"Mauritania",
	"Mauritius",
	"Mayotte",
	"Mexico",
	"Micronesia (Federated States of)",
	"Moldova (the Republic of)",
	"Monaco",
	"Mongolia",
	"Montenegro",
	"Montserrat",
	"Morocco",
	"Mozambique",
	"Myanmar",
	"Namibia",
	"Nauru",
	"Nepal",
	"Netherlands (the)",
	"New Caledonia",
	"New Zealand",
	"Nicaragua",
	"Niger (the)",
	"Nigeria",
	"Niue",
	"Norfolk Island",
	"Northern Mariana Islands (the)",
	"Norway",
	"Oman",
	"Pakistan",
	"Palau",
	"Palestine, State of",
	"Panama",
	"Papua New Guinea",
	"Paraguay",
	"Peru",
	"Philippines (the)",
	"Pitcairn",
	"Poland",
	"Portugal",
	"Puerto Rico",
	"Qatar",
	"Republic of North Macedonia",
	"Romania",
	"Russian Federation (the)",
	"Rwanda",
	"Réunion",
	"Saint Barthélemy",
	"Saint Helena, Ascension and Tristan da Cunha",
	"Saint Kitts and Nevis",
	"Saint Lucia",
	"Saint Martin (French part)",
	"Saint Pierre and Miquelon",
	"Saint Vincent and the Grenadines",
	"Samoa",
	"San Marino",
	"Sao Tome and Principe",
	"Saudi Arabia",
	"Senegal",
	"Serbia",
	"Seychelles",
	"Sierra Leone",
	"Singapore",
	"Sint Maarten (Dutch part)",
	"Slovakia",
	"Slovenia",
	"Solomon Islands",
	"Somalia",
	"South Africa",
	"South Georgia and the South Sandwich Islands",
	"South Sudan",
	"Spain",
	"Sri Lanka",
	"Sudan (the)",
	"Suriname",
	"Svalbard and Jan Mayen",
	"Sweden",
	"Switzerland",
	"Syrian Arab Republic",
	"Taiwan",
	"Tajikistan",
	"Tanzania",
	"Thailand",
	"Timor-Leste",
	"Togo",
	"Tokelau",
	"Tonga",
	"Trinidad and Tobago",
	"Tunisia",
	"Turkey",
	"Turkmenistan",
	"Turks and Caicos Islands (the)",
	"Tuvalu",
	"Uganda",
	"Ukraine",
	"United Arab Emirates (the)",
	"United Kingdom of Great Britain and Northern Ireland (the)",
	"United States Minor Outlying Islands (the)",
	"United States of America (the)",
	"Uruguay",
	"Uzbekistan",
	"Vanuatu",
	"Venezuela (Bolivarian Republic of)",
	"Viet Nam",
	"Virgin Islands (British)",
	"Virgin Islands (U.S.)",
	"Wallis and Futuna",
	"Western Sahara",
	"Yemen",
	"Zambia",
	"Zimbabwe",
        "Åland Islands"
];

const eastAfricaCountries = [
    "Burundi",
    "Comoros (the)",
    "Djibouti",
    "Eritrea",
    "Ethiopia",
    "Kenya",
    "Rwanda",
    "Somalia",
    "South Sudan",
    "Sudan (the)",
    "Tanzania",
    "Uganda",
    "uganda"
];

const europeCountries = [
    "Albania","Andorra","Austria","Belarus","Belgium","Bosnia and Herzegovina","Bulgaria","Croatia","Cyprus","Czechia","Denmark","Estonia","Finland","France","Germany","Greece","Hungary","Iceland","Ireland","Italy","Latvia","Liechtenstein","Lithuania","Luxembourg","Malta","Moldova (the Republic of)","Monaco","Montenegro","Netherlands (the)","Republic of North Macedonia","Norway","Poland","Portugal","Romania","Russian Federation (the)","San Marino","Serbia","Slovakia","Slovenia","Spain","Sweden","Switzerland","Ukraine","United Kingdom of Great Britain and Northern Ireland (the)","Holy See (the)","Faroe Islands (the)","Gibraltar","Guernsey","Jersey","Isle of Man","Åland Islands","Svalbard and Jan Mayen"
];

const americasCountries = [
    "Antigua and Barbuda","Argentina","Bahamas (the)","Barbados","Belize","Bolivia (Plurinational State of)","Brazil","Canada","Cayman Islands (the)","Chile","Colombia","Costa Rica","Cuba","Dominica","Dominican Republic (the)","Ecuador","El Salvador","Falkland Islands (the) [Malvinas]","Grenada","Guadeloupe","Guatemala","Guyana","Haiti","Honduras","Jamaica","Mexico","Nicaragua","Panama","Paraguay","Peru","Puerto Rico","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Suriname","Trinidad and Tobago","United States of America (the)","Uruguay","Venezuela (Bolivarian Republic of)","Virgin Islands (British)","Virgin Islands (U.S.)","Bermuda","Martinique","Saint Pierre and Miquelon","Saint Barthélemy","Saint Martin (French part)","Turks and Caicos Islands (the)","Bonaire, Sint Eustatius and Saba","Aruba","Curaçao"
];

const allowedCountries = [...eastAfricaCountries, ...europeCountries, ...americasCountries];

export const countryList = allCountries.filter(c => allowedCountries.includes(c));

const allNationalities = [
    "Albanian",
    "American",
    "Argentine",
    "Armenian",
    "Australian",
    "Austrian",
    "Azerbaijani",
    "Bahamian",
    "Barbadian",
    "Belgian",
    "Bolivian",
    "Bosnian",
    "Brazilian",
    "British",
    "Bulgarian",
    "Canadian",
    "Chilean",
    "Chinese",
    "Colombian",
    "Croatian",
    "Czech",
    "Danish",
    "Dutch",
    "Ecuadorean",
    "Eritrean",
    "Estonian",
    "Ethiopian",
    "Filipino",
    "Finnish",
    "French",
    "Georgian",
    "German",
    "Ghanaian",
    "Greek",
    "Grenadian",
    "Haitian",
    "Hungarian",
    "Icelandic",
    "Iranian",
    "Iraqi",
    "Irish",
    "Israeli",
    "Italian",
    "Kazakh",
    "Kenyan",
    "Nigerian",
    "Rwandan",
    "South African",
    "Tanzanian",
    "Ugandan",
];

const eastAfricaNationalities = ["Burundian","Comoran","Djiboutian","Eritrean","Ethiopian","Kenyan","Rwandan","Sudanese","Tanzanian","Ugandan"];
const europeNationalities = ["Albanian","Andorran","Austrian","Belarusian","Belgian","Bosnian","Bulgarian","Croatian","Cypriot","Czech","Danish","Estonian","Finnish","French","German","Greek","Hungarian","Icelandic","Irish","Italian","Latvian","Liechtenstein","Lithuanian","Luxembourgish","Maltese","Moldovan","Monacan","Montenegrin","Dutch","Macedonian","Norwegian","Polish","Portuguese","Romanian","Russian","San Marinese","Serbian","Slovak","Slovenian","Spanish","Swedish","Swiss","Ukrainian","British"];
const americasNationalities = ["American","Argentine","Bahamian","Barbadian","Belizean","Bolivian","Brazilian","Canadian","Chilean","Colombian","Costa Rican","Cuban","Dominican","Ecuadorean","Grenadian","Guatemalan","Guyanese","Haitian","Honduran","Jamaican","Mexican","Nicaraguan","Panamanian","Paraguayan","Peruvian","Puerto Rican","Salvadoran"];
const allowedNationalities = [...eastAfricaNationalities, ...europeNationalities, ...americasNationalities];
export const nationalitieslist = allNationalities.filter(n => allowedNationalities.includes(n));

const zodiacsigns = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpius", "Sagittarius", "Capricornus", "Aquarius", "Pisces"]

export const packages = [
    "Bronze",
    "Silver",
    "Gold",
    "Platinum",
    "Diamond",
    "Emerald",
    "Ruby"
]


export const kenyaBrandPreferences = [
    "MAC / Fenty Beauty",
    "Samsung / iPhone",
    "Safaricom",
    "Sarova Hotels / Serena / Radisson",
    "Toyota / Mercedes",
    "Uber / Bolt",
    "Netflix / Showmax"
]

export const tanzaniaBrandPreferences = [
    "MAC / Fenty Beauty",
    "Samsung / iPhone",
    "Tigo / Vodacom",
    "Hyatt / Serena / Sea Cliff",
    "Toyota / Mercedes",
    "Uber / Bolt",
    "Azam TV / DStv"
]

export const ugandaBrandPreferences = [
    "MAC / Fenty Beauty",
    "Samsung / iPhone",
    "MTN / Airtel",
    "Serena / Speke Resort",
    "Toyota / Mercedes",
    "Uber / Bolt",
    "DStv / GOtv"
]

export const kenyaResidentialAreas = [
    "Kilimani",
    "Kileleshwa",
    "Westlands",
    "Karen",
    "Runda",
    "Other"
]

export const tanzaniaResidentialAreas = [
    "Mikocheni","Masaki","Oyster Bay","Ada Estate","Kinondoni B","Kijitonyama","Sinza",
    "Kariakoo","Upanga","Tabata","Buguruni","Ilala Boma",
    "Mbagala","Kurasini","Chang’ombe","Kigamboni",
    "Njiro","Sekei","Sakina","Themi","Kaloleni","Kijenge","Sanawari",
    "Isamilo","Kirumba","Igoma","Nyegezi","Mkolani","Nyamagana","Buzuruga",
    "Area C","Area D","Kikuyu North","Chang’ombe (Dodoma)","Nkuhungu","Mlimwa",
    "Stone Town","Mbweni","Fumba","Mazizini","Kisauni","Mtoni","Bububu",
    "Forest","Soweto","Iyela","Mwanjelwa","Ilomba",
    "Kihonda","Mazimbu","Mji Mpya","Kingolwira",
    "Other"
]

export const ugandaResidentialAreas = [
    "Kololo","Naguru","Bukoto","Bugolobi","Muyenga",
    "Ntinda","Naalya","Lubowa","Entebbe","Other"
]

export const cityOptionsByCountry = {
    kenya: [
        "Nairobi",
        "Mombasa",
        "Kisumu",
        "Eldoret",
        "Nakuru",
        "Thika",
        "Malindi",
        "Kitale",
        "Garissa",
        "Kakamega",
        "Nyeri",
        "Machakos",
        "Naivasha",
        "Meru",
        "Embu",
        "Kericho",
        "Migori",
        "Homa Bay",
        "Isiolo",
        "Lamu",
        "Other"
    ],
    tanzania: [
        "Dar es Salaam",
        "Arusha",
        "Dodoma",
        "Mwanza",
        "Zanzibar City",
        "Mbeya",
        "Morogoro",
        "Tanga",
        "Kigoma",
        "Mtwara",
        "Tabora",
        "Iringa",
        "Songea",
        "Sumbawanga",
        "Shinyanga",
        "Kahama",
        "Musoma",
        "Bukoba",
        "Singida",
        "Other"
    ],
    uganda: [
        "Kampala",
        "Entebbe",
        "Jinja",
        "Mbarara",
        "Gulu",
        "Lira",
        "Arua",
        "Mbale",
        "Fort Portal",
        "Masaka",
        "Kabale",
        "Soroti",
        "Hoima",
        "Kasese",
        "Mukono",
        "Other"
    ],
    somalia: [
        "Mogadishu",
        "Hargeisa",
        "Bosaso",
        "Kismayo",
        "Galkayo",
        "Baidoa",
        "Berbera",
        "Beledweyne",
        "Burao",
        "Garowe",
        "Jowhar",
        "Marka",
        "Las Anod",
        "Other"
    ],
    rwanda: [
        "Kigali",
        "Huye",
        "Musanze",
        "Rubavu",
        "Nyagatare",
        "Rwamagana",
        "Gisenyi",
        "Gicumbi",
        "Kayonza",
        "Rusizi",
        "Nyamata",
        "Other"
    ],
    burundi: [
        "Gitega",
        "Bujumbura",
        "Ngozi",
        "Ruyigi",
        "Rumonge",
        "Kayanza",
        "Muyinga",
        "Bururi",
        "Makamba",
        "Muramvya",
        "Cibitoke",
        "Other"
    ],
    comorros: [
        "Moroni",
        "Mutsamudu",
        "Fomboni",
        "Domoni",
        "Sima",
        "Ouani",
        "Itsandra",
        "Iconi",
        "Other"
    ],
    "south sudan": [
        "Juba",
        "Wau",
        "Malakal",
        "Bor",
        "Yei",
        "Bentiu",
        "Aweil",
        "Torit",
        "Rumbek",
        "Kuajok",
        "Other"
    ],
    ethiopea: [
        "Addis Ababa",
        "Dire Dawa",
        "Mekelle",
        "Gondar",
        "Bahir Dar",
        "Hawassa",
        "Adama",
        "Jimma",
        "Dessie",
        "Harar",
        "Shashamane",
        "Nekemte",
        "Other"
    ],
    eritrea: [
        "Asmara",
        "Keren",
        "Massawa",
        "Assab",
        "Mendefera",
        "Barentu",
        "Dekemhare",
        "Adi Keyh",
        "Other"
    ],
    default: ["Other"]
}

export const kenyaMonthlyIncome = [
    "0-10k KES",
    "10-25k KES",
    "25-50k KES",
    "50-100k KES",
    "100-150k KES",
    "150-200k KES",
    "200-250k KES",
    "250-300k KES",
    "300-350k KES",
    "350-400k KES",
    "400-450k KES",
    "450-500k KES",
    "500k+ KES"
]

export const kenyaEducationLevels = [
    "No formal education",
    "Pre-primary / ECDE",
    "Primary incomplete",
    "Primary complete / KCPE",
    "Junior Secondary School",
    "Secondary incomplete",
    "Secondary complete / KCSE",
    "Artisan certificate",
    "Craft certificate",
    "Certificate",
    "Diploma",
    "Higher National Diploma",
    "Bachelor’s degree",
    "Postgraduate diploma",
    "Master’s degree",
    "Doctorate / PhD",
    "Madrasa / Islamic studies",
    "Other"
]

export const kenyaMonthlyIncomeAfterTax = [
    "Above KSh 300,000 → High income – financially very stable",
    "Between KSh 150,000 and 300,000 → Comfortable income – stable and self-sufficient",
    "Between KSh 80,000 and 150,000 → Moderate income – stable but budget-conscious",
    "Below KSh 80,000 → Low income – may face financial strain with large commitments"
]

export const tanzaniaMonthlyIncome = [
    "Less than 1,000,000 TSh",
    "1,000,000 – 2,000,000 TSh",
    "2,000,000 – 4,000,000 TSh",
    "4,000,000+ TSh"
]

export const ugandaMonthlyIncome = [
    "Less than 1,500,000 UGX",
    "1,500,000 – 3,000,000 UGX",
    "3,000,000 – 6,000,000 UGX",
    "6,000,000+ UGX"
]

export const tanzaniaMonthlyIncomeAfterTax = [
    "Above TSh 6,000,000 → High income – financially very stable",
    "Between TSh 3,000,000 and 6,000,000 → Comfortable income – stable and self-sufficient",
    "Between TSh 1,500,000 and 3,000,000 → Moderate income – stable but budget-conscious",
    "Below TSh 1,500,000 → Low income – may face financial strain with large commitments"
]

export const ugandaMonthlyIncomeAfterTax = [
    "Above UGX 10,000,000 → High income – financially very stable",
    "Between UGX 5,000,000 and 10,000,000 → Comfortable income – stable and self-sufficient",
    "Between UGX 2,000,000 and 5,000,000 → Moderate income – stable but budget-conscious",
    "Below UGX 2,000,000 → Low income – may face financial strain with large commitments"
]

const sectionOrder = [
    "Basic Information",
    "Location",
    "Education and Career",
    "Financial Background",
    "Lifestyle and Interests",
    "Travel",
    "Language and Communication",
    "Family Background",
    "Mental Health and Therapy",
    "Personal and Relationship Goals",
    "Relationship Preferences",
    "General Spouse Preferences",
    "Personality and Character",
    "Financial and Career Expectations",
    "Family and Future Plans",
    "Compatibility and Long-Term Vision",
    "Additional Information",
    "To Future Spouse",
    "Images"
];


const religions = [
    "Christian",
    "Muslim",
    "Buddhist",
    "Hindu",
    "Traditional Africa",
    "Atheist",
    "I don't mind his religion"
]

const womanreligions = ["Christian", "Muslim", "Traditional African Religion", "Atheist", "Other"]

const womanethnicities = [
    "African",
    "European",
    "Asian",
    "Oriental",
    "Mixed Race",
    "Other",
    "Ugandan"
]

const relationshipStatusOptionsByGender = {
    male: [
        "Single",
        "Divorced",
        "Married",
        "Single, never married with children",
        "Married with children"
    ],
    female: [
        "Single",
        "Divorced",
        "Single, never married with children"
    ]
}

const QUESTION_MEANINGS = {
    "Preferred Age Range": "Choose the age range you feel most comfortable marrying. This helps us suggest people you’re most likely to connect with.",
    "Preferred Location": "Choose how close you want your partner to be. This helps reduce long-distance challenges.",
    "Current Location": "You might be from one country or city but currently living somewhere else. Share where you are currently based so we match you accurately.",
    "Willing to Relocate": "Let us know if you are open to moving to another city or country after marriage.",
    "Open to Long-Distance": "Some couples start long-distance and later close the distance through relocation. Choose what works for you.",
    "Languages You Speak": "Language matters for comfort, communication, and daily connection.",
    "Tribe / Ethnicity Preference": "If culture is important to you, you can share your preference. If not, choose “No preference.”",
    "What is your nationality?": "Your own nationality helps complete your profile and may be useful for future matching and profile context.",
    "Height Preference": "This is purely a personal attraction preference.",
    "Body Type Preference": "This helps match physical preference respectfully without judgment.",
    "Appearance Priority": "This helps us understand whether physical attraction matters a lot to you or if character matters more.",
    "Madhahab ": "If you follow a madhhab closely, this helps avoid disagreements in worship and daily rulings.",
    "Level of Practice": "This helps match you with someone who lives Islam similarly to you, not someone who will pressure you or feel pressured.",
    "Salah (Prayer)": "Salah is a major part of Islamic life. Choose what matters most to you in a spouse.",
    "Jum’ah (Friday Prayer) – For Men": "This helps match women who value a man’s commitment to the mosque and Islamic responsibility.",
    "Qur’an Level": "This isn’t about perfection. It helps match someone who shares your relationship with the Qur’an.",
    "Interest in Islamic Learning": "Some people want a spouse who is always learning and growing in deen.",
    "Hijab Preference (Men selecting Women)": "If hijab matters to you, you can select it respectfully.",
    "Niqab Preference (Optional)": "Some brothers prefer niqab, some sisters wear it, and some don’t. This helps match expectations early.",
    "Beard Preference (Women selecting Men)": "A beard can be a religious or attraction preference.",
    "Avoids Music": "Some Muslims avoid music completely. Others are relaxed. This helps match lifestyle comfort.",
    "Avoids Free-Mixing": "This refers to avoiding unnecessary mixing with the opposite gender in social life or lifestyle.",
    "Islamic Home Environment": "This is about the general atmosphere in the home: prayer, modesty, values, and Islamic tone.",
    "Halaal Lifestyle Strictness": "This measures how strict you are about halaal standards.",
    "Ready for Marriage Now": "This indicates whether marriage can happen soon, or whether more time is needed before making a serious commitment.",
    "Preferred Marriage Timeline": "This sets expectations on how soon marriage should happen after getting to know someone, so serious people don’t waste time.",
    "Engagement Period Preference": "This helps match someone who prefers a quick, serious process with someone who wants more time before nikah.",
    "Family Involvement Timing": "This shows when families should be involved in the process, which can protect seriousness and reduce hidden intentions.",
    "Structured Communication Preference": "This shows whether someone prefers a respectful, guided approach to communication instead of casual, unlimited chatting.",
    "Seriousness Level": "This helps separate people who want marriage from people who only want attention, entertainment, or endless talking.",
    "Children Preference": "This is a life-direction preference. Wanting children or not wanting children can strongly affect long-term compatibility.",
    "How Soon to Have Children": "This clarifies expectations about starting a family and helps avoid mismatched planning after marriage.",
    "Open to Marrying Someone Divorced": "Many good Muslims are divorced. This option helps keep preferences honest and realistic.",
    "Open to Marrying Someone with Children": "Marrying someone with children comes with responsibility, time, and emotional maturity. This preference matters for stability.",
    "Open to Marrying Someone Widowed": "Some people are widowed and still want companionship and family. This option avoids unnecessary stigma.",
    "Polygyny Preference (Women Only)": "Polygyny is a form of marriage in which one man has multiple wives at the same time. This shows whether marriage must be strictly monogamous, or whether polygyny is something that could be accepted.",
    "Monogamy Only Preference": "This clearly states that marriage must be one-to-one, with no second wife in the future.",
    "Alcohol": "Alcohol is a major lifestyle boundary and often a complete dealbreaker for many Muslims. This sets clarity early.",
    "Cigarettes (Smoking)": "Smoking affects health, smell, finances, and comfort at home.",
    "Shisha / Hookah": "Some consider it the same as smoking. Others see it as occasional social behaviour.",
    "Vaping": "Many people dislike vaping because it’s similar to smoking and can become addictive. .",
    "Weed / Drugs": "Drug use can ruin stability, trust, and deen.",
    "Gambling": "Gambling destroys finances and trust, and is clearly haram.",
    "Parties / Club Lifestyle": "This matches lifestyle energy and modesty standards.",
    "Music Lifestyle": "Some people avoid it completely, others are relaxed. This sets expectations for the home environment. ",
    "Social Media Posting": "This affects privacy, jealousy issues, and how a relationship is shown to the public.",
    "Friends of the Opposite Gender": "Many Muslims prefer strict boundaries to avoid future insecurity and conflict.",
    "Free-Mixing (Work & Social)": "Some people avoid mixed environments, others accept it for work but keep boundaries.",
    "Hijab (Women)": "This helps match expectations respectfully.",
    "Hair Length Preference (Women)": "Hair preferences are about personal attraction. ",
    "Hair Style Preference (Women)": "Some people have strong preferences about styling.",
    "Facial Hair Preference (Men)": "Some women like a clean look, others prefer a beard.",
    "Eye Colour Preference (Optional)": "Soft attraction preference.",
    "Skin Tone Preference (Optional)": "Sensitive preference, optional.",
    "Fitness Lifestyle Preference": "This matches health habits and daily routines. ",
};

const profileQuestions = [
    {
        section: "Welcome to Zawajiyyah",
        question: "This is a space for those who are serious about marriage.\nCreate your profile, answer honestly, and move with intention. Every step is designed to help you find a compatible partner with clarity and dignity. Begin when ready.",
        type: "interlude",
        fieldid: "welcometozawajiyyah1",
        showInEdit: false,
        
        flag: 'This is a space for those who are serious about marriage.\nCreate your profile, answer honestly, and move with intention. Every step is designed to help you find a compatible partner with clarity and dignity. Begin when ready.',
        placeholderFlag: 'This is a space for those who are serious about marriage.\nCreate your profile, answer honestly, and move with intention. Every step is designed to help you find a compatible partner with clarity and dignity. Begin when ready.',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Age",
        type: "age",
        fieldid: "age1",
        min: 18,
        max: 80,
        
        flag: 'Age',
        placeholderFlag: 'Age',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Race",
        type: "choice",
        fieldid: "race1",
        
        flag: 'Race',
        placeholderFlag: 'Race',
        answers: ["Swahili", "Somali", "African", "Arab", "Indian", "European", "Other"]
    },
    {
        section: "Bio Details",
        question: "What is your nationality?",
        type: "datalistinput",
        fieldid: "ethnicity1",
        
        placeholder: "Enter your nationality:",
        flag: 'What is your nationality?',
        placeholderFlag: 'What is your nationality?',
        answers: nationalitieslist
    },
    {
        section: "Bio Details",
        question: "What is your tribe?",
        type: "datalistinput",
        fieldid: "tribe1",
        placeholder: "Enter your tribe, or choose N/A if this does not apply:",
        
        flag: 'What is your tribe?',
        placeholderFlag: 'What is your tribe?',
        answers: ["N/A"]
    },
    // {
    //     section: "Bio Details",
    //     question: "Your marital Status:",
    //     type: "choice",
    //     fieldid: "familybackground4",
    //     
    //     flag: 'Your marital Status:',
    //     answers: ["Never Married", "Divorced", "Widowed"]
    // },
    {
        section: "Bio Details",
        question: "Current Location",
        type: "text",
        fieldid: "currentlocation1",
        
        placeholder: "Enter text:",
        flag: 'Current Location',
        placeholderFlag: 'Current Location',
        answers: []
    },
    {
        section: "Bio Details",
        question: "First and Last Name",
        type: "name",
        fieldid: "firstname1",
        fieldid2: "lastname1",
        
        placeholder: "Enter text:",
        flag: 'First and Last Name',
        placeholderFlag: 'First and Last Name',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Phone Number",
        type: "text",
        fieldid: "phonenumber1",
        
        flag: 'Phone Number',
        placeholderFlag: 'Phone Number',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Email",
        type: "text",
        fieldid: "EMAIL",
        
        flag: 'Email',
        placeholderFlag: 'Email',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Relationship Status",
        type: "choice",
        fieldid: "relationshipstatus1",
        
        flag: 'Relationship Status',
        placeholderFlag: 'Relationship Status',
        answers: ["Single", "Divorced", "Married"]
    },
    {
        section: "Bio Details",
        question: "Profession / Occupation",
        type: "text",
        fieldid: "profession1",
        
        placeholder: "Enter text:",
        flag: 'Profession / Occupation',
        placeholderFlag: 'Profession / Occupation',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Income Level",
        type: "choice",
        fieldid: "monthlyincome1",
        
        flag: 'Income Level',
        placeholderFlag: 'Income Level',
        answers: kenyaMonthlyIncome
    },
    {
        section: "Bio Details",
        question: "Level of Education",
        type: "choice",
        fieldid: "educationlevel1",
        
        flag: 'Level of Education',
        placeholderFlag: 'Level of Education',
        answers: kenyaEducationLevels
    },
    {
        section: "Bio Details",
        question: "Relationship to person you are signing up for.",
        type: "choice",
        fieldid: "walisrelationshiptoperson1",
        
        flag: 'Relationship to person you are signing up for.',
        placeholderFlag: 'Relationship to person you are signing up for.',
        answers: [
            "Wali",
            "Relative",
            "Friend",
            "Other"
        ]
    },
    {
        section: "Basic Matching Preferences",
        question: "You are now entering the matching preferences section.\nThis stage defines the qualities and characteristics you value in a potential husband/wife.Selections made here will guide the compatibility and relevance of your matches.\nApproach this section with clarity and intention. Thoughtful choices lead to stronger alignment and more meaningful connections.",
        type: "interlude",
        fieldid: "basicmatchingpreferences1",
        showInEdit: false,
        
        flag: 'You are now entering the matching preferences section.\\nThis stage defines the qualities and characteristics you value in a potential husband/wife.Selections made here will guide the compatibility and relevance of your matches.\\nApproach this section with clarity and intention. Thoughtful choices lead to stronger alignment and more meaningful connections.',
        placeholderFlag: 'You are now entering the matching preferences section.\\nThis stage defines the qualities and characteristics you value in a potential husband/wife.Selections made here will guide the compatibility and relevance of your matches.\\nApproach this section with clarity and intention. Thoughtful choices lead to stronger alignment and more meaningful connections.',
        answers: []
    },
    {
        section: "Basic Matching Preferences",
        question: "Preferred Age Range Minimum",
        type: "age",
        fieldid: "preferredagerangemin1",
        min: 18,
        max: 80,
        
        flag: 'Preferred Age Range Minimum',
        placeholderFlag: 'Preferred Age Range Minimum',
        answers: []
    },
    {
        section: "Basic Matching Preferences",
        question: "Preferred Age Range Maximum",
        type: "age",
        fieldid: "preferredagerangemax1",
        min: 18,
        max: 80,
        
        flag: 'Preferred Age Range Maximum',
        placeholderFlag: 'Preferred Age Range Maximum',
        answers: []
    },
    {
        section: "Basic Matching Preferences",
        question: "Which race would you primarily prefer?",
        type: "choice",
        fieldid: "preferredraceprimary1",
        
        flag: 'Which race would you primarily prefer?',
        placeholderFlag: 'Which race would you primarily prefer?',
        answers: ["Swahili", "Somali", "African", "Arab", "Indian", "Any", "Other"]
    },
    {
        section: "Basic Matching Preferences",
        question: "Would you like someone from your tribe or another?",
        type: "choice",
        fieldid: "tribepreference1",
        
        flag: 'Would you like someone from your tribe or another?',
        placeholderFlag: 'Would you like someone from your tribe or another?',
        answers: ["Same", "Any", "Other"]
    },
    {
        section: "Basic Matching Preferences",
        question: "Willing to Relocate",
        type: "choice",
        fieldid: "willingtorelocate1",
        
        flag: 'Willing to Relocate',
        placeholderFlag: 'Willing to Relocate',
        answers: ["Yes", "No", "Depends on country"]
    },
    {
        section: "Basic Matching Preferences",
        question: "Open to Long-Distance",
        type: "choice",
        fieldid: "longdistanceopenness1",
        
        flag: 'Open to Long-Distance',
        placeholderFlag: 'Open to Long-Distance',
        answers: ["Yes", "No", "Only short-term until relocation"]
    },
    {
        section: "Basic Matching Preferences",
        question: "Languages You Speak",
        type: "choice",
        fieldid: "languagesyouspeak1",
        pick: 9,
        
        flag: 'Languages You Speak',
        placeholderFlag: 'Languages You Speak',
        answers: ["English", "Kiswahili", "Somali", "Arabic", "Amharic", "Oromo", "French", "Turkish", "Other"]
    },
    {
        section: "Basic Matching Preferences",
        question: "Appearance Priority",
        type: "choice",
        fieldid: "appearancepriority1",
        
        flag: 'Appearance Priority',
        placeholderFlag: 'Appearance Priority',
        answers: ["Not important", "Somewhat important", "Very important"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Madhahab ",
        type: "choice",
        fieldid: "madhhabpreference1",
        
        flag: 'Madhahab ',
        placeholderFlag: 'Madhahab ',
        answers: [
            "Maliki",
            "Shafi'i",
            "Hanbali",
            "Hanifi",
            "None of the above"
        ]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Level of Practice",
        type: "choice",
        fieldid: "levelofpractice1",
        
        flag: 'Level of Practice',
        placeholderFlag: 'Level of Practice',
        answers: ["Very practising", "Practising", "Moderate", "Improving", "No preference"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Salah (Prayer)",
        type: "choice",
        fieldid: "salahprayer1",
        
        flag: 'Salah (Prayer)',
        placeholderFlag: 'Salah (Prayer)',
        answers: ["Must pray five times a day.", "Must pray, even if occasionally missed", "Open to someone still improving in prayer"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Jum’ah (Friday Prayer) – For Men",
        type: "choice",
        fieldid: "jummahformen1",
        
        flag: 'Jum’ah (Friday Prayer) – For Men',
        placeholderFlag: 'Jum’ah (Friday Prayer) – For Men',
        answers: ["Must attend weekly", "Prefer weekly", "Sometimes okay"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Hijab Preference (Men selecting Women)",
        type: "choice",
        fieldid: "hijabpreferencemen1",
        
        flag: 'Hijab Preference (Men selecting Women)',
        placeholderFlag: 'Hijab Preference (Men selecting Women)',
        answers: ["Must wear hijab", "Prefer hijab", "Open to discussion"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Niqab Preference (Optional)",
        type: "choice",
        fieldid: "niqabpreference1",
        
        flag: 'Niqab Preference (Optional)',
        placeholderFlag: 'Niqab Preference (Optional)',
        answers: ["Prefer niqab", "Prefer hijab only", "Not comfortable with niqab"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Beard Preference (Women selecting Men)",
        type: "choice",
        fieldid: "beardpreferencewomen1",
        
        flag: 'Beard Preference (Women selecting Men)',
        placeholderFlag: 'Beard Preference (Women selecting Men)',
        answers: ["Prefer beard", "Prefer clean-shaven", "No preference"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "Avoids Free-Mixing",
        type: "choice",
        fieldid: "avoidsfreemixing1",
        
        flag: 'Avoids Free-Mixing',
        placeholderFlag: 'Avoids Free-Mixing',
        answers: ["I’m seeking someone who strictly avoids mixed social settings", "I’m seeking someone who generally avoids mixed social settings", "I’m flexible, but modesty is important to me"]
    },
    {
        section: "Islamic Practice Preferences",
        question: "What is your desire with regards to the Islamic Home Environment you and your spouse will create?",
        type: "choice",
        fieldid: "islamichomeenvironment1",
        
        flag: 'What is your desire with regards to the Islamic Home Environment you and your spouse will create?',
        placeholderFlag: 'What is your desire with regards to the Islamic Home Environment you and your spouse will create?',
        answers: ["Very Islamic", "Balanced", "Improving together"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "What is your Preferred Marriage Timeline?",
        type: "choice",
        fieldid: "preferredmarriagetimeline1",
        
        flag: 'What is your Preferred Marriage Timeline?',
        placeholderFlag: 'What is your Preferred Marriage Timeline?',
        answers: ["0–3 months", "3–6 months", "6–12 months", "1–2 years", "Not sure yet"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "At what stage are you comfortable with Family Involvement?",
        type: "choice",
        fieldid: "familyinvolvementtiming1",
        
        flag: 'At what stage are you comfortable with Family Involvement?',
        placeholderFlag: 'At what stage are you comfortable with Family Involvement?',
        answers: ["Must involve early", "Prefer early", "Later is fine", "Not comfortable"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "Children Preference",
        type: "choice",
        fieldid: "childrenpreference1",
        
        flag: 'Children Preference',
        placeholderFlag: 'Children Preference',
        answers: ["Yes", "No", "Maybe", "Already have children"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "Are you Open to Marrying Someone Divorced?",
        type: "choice",
        fieldid: "opentodivorced1",
        
        flag: 'Are you Open to Marrying Someone Divorced?',
        placeholderFlag: 'Are you Open to Marrying Someone Divorced?',
        answers: ["Yes", "No"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "Are you Open to Marrying Someone with Children?",
        type: "choice",
        fieldid: "opentowithchildren1",
        
        flag: 'Are you Open to Marrying Someone with Children?',
        placeholderFlag: 'Are you Open to Marrying Someone with Children?',
        answers: ["Yes", "No"]
    },
    {
        section: "Marriage Intent & Timeline",
        question: "What is your preference with regards to Polygyny?",
        type: "choice",
        fieldid: "polygynypreference1",
        whatItMeans: "Polygyny means: a form of marriage where one man is married to more than one wife at the same time.",
        
        flag: 'What is your preference with regards to Polygyny?',
        placeholderFlag: 'What is your preference with regards to Polygyny?',
        answers: ["Not open to it", "Maybe (depends)", "Open to it"]
    },
    {
        section: "Potential Red Flags",
        question: "What is your position on Alcohol with regards to your spouse?",
        type: "choice",
        fieldid: "alcohol1",
        
        flag: 'What is your position on Alcohol with regards to your spouse?',
        placeholderFlag: 'What is your position on Alcohol with regards to your spouse?',
        answers: [
            "Must not drink at all",
            "Never drinks, even socially",
            "Used to drink but stopped",
            "Drinks occasionally (not okay for me)"
        ]
    },
    {
        section: "Potential Red Flags",
        question: "What is your position on Cigarettes (Smoking) with regards to your spouse?",
        type: "choice",
        fieldid: "cigarettes1",
        
        flag: 'What is your position on Cigarettes (Smoking) with regards to your spouse?',
        placeholderFlag: 'What is your position on Cigarettes (Smoking) with regards to your spouse?',
        answers: [
            "Must be a non-smoker",
            "Prefer non-smoker",
            "Okay if trying to quit",
            "Okay occasionally"
        ]
    },
    {
        section: "Potential Red Flags",
        question: "What is your position on Parties and the Club Lifestyle with regards to your spouse?",
        type: "choice",
        fieldid: "parties1",
        
        flag: 'What is your position on Parties and the Club Lifestyle with regards to your spouse?',
        placeholderFlag: 'What is your position on Parties and the Club Lifestyle with regards to your spouse?',
        answers: [
            "Must not go clubbing/partying",
            "Occasionally goes to events (not clubs)",
            "Used to party but stopped",
            "No preference"
        ]
    },
    {
        section: "Potential Red Flags",
        question: "What is your position on friends of the Opposite Gender with regards to your spouse?",
        type: "choice",
        fieldid: "oppositegenderfriends1",
        
        flag: 'What is your position on friends of the Opposite Gender with regards to your spouse?',
        placeholderFlag: 'What is your position on friends of the Opposite Gender with regards to your spouse?',
        answers: [
            "Must not have close friends of the opposite gender",
            "Casual acquaintances are okay",
            "Open to discussion"
        ]
    },
    {
        section: "Personal / Physical Preferences (Attraction Filters)",
        question: "Facial Hair Preference (Men)",
        type: "choice",
        fieldid: "facialhairpref1",
        
        flag: 'Facial Hair Preference (Men)',
        placeholderFlag: 'Facial Hair Preference (Men)',
        answers: [
            "Full beard preferred",
            "Light beard preferred",
            "Clean-shaven preferred",
            "No preference"
        ]
    },
    {
        section: "Personal / Physical Preferences (Attraction Filters)",
        question: "Body Type Preference",
        type: "choice",
        fieldid: "bodytypepref1",
        pick: 5,
        
        flag: 'Body Type Preference',
        placeholderFlag: 'Body Type Preference',
        answers: [
            "Slim",
            "Average",
            "Curvy",
            "Athletic",
            "Plus-size",
            "No preference"
        ]
    },
    {
        section: "To My Future Spouse",
        question: "Is there anything you would like to say to your future spouse?",
        type: "longtext",
        fieldid: "tomyfuturespouse1",
        minLength: 150,
        
        placeholder: "Enter text:",
        flag: 'Is there anything you would like to say to your future spouse?',
        placeholderFlag: 'Is there anything you would like to say to your future spouse?',
        answers: []
    },
    {
        section: "Images",
        question: "Upload up to 5 profile photos:",
        type: "image",
        fieldid: "profilepic1",
        
        flag: 'Upload up to 5 profile photos:',
        placeholderFlag: 'Upload up to 5 profile photos:',
        answers: []
    },
    {
        section: "Bio Details",
        question: "Password",
        type: "pass",
        fieldid: "password",
        
        flag: 'Password',
        placeholderFlag: 'Password',
        answers: []
    },
    {
        section: "Terms & Conditions",
        question: "I have read and agree to the Zawajiyyah Terms of Service, Terms and Conditions, and User Agreement.",
        type: "checkbox",
        fieldid: "termsaccepted1",
        
        flag: 'I have read and agree to the Zawajiyyah Terms of Service, Terms and Conditions, and User Agreement.',
        placeholderFlag: 'I have read and agree to the Zawajiyyah Terms of Service, Terms and Conditions, and User Agreement.',
        answers: []
    }];
const MALE_EXCLUDED_FIELD_IDS = new Set([
    "jummahformen1",
    "beardpreferencewomen1",
    "polygynypreference1",
    "facialhairpref1"
]);

const FEMALE_EXCLUDED_FIELD_IDS = new Set([
    "hijabpreferencemen1",
    "niqabpreference1"
]);

const applyQuestionRulesByGender = (questions, gender) => (
    questions
    .filter((question) => {
        if (question.fieldid === "avoidsmusic1") {
            return false;
        }
        if (gender === "Male" && MALE_EXCLUDED_FIELD_IDS.has(question.fieldid)) {
            return false;
        }
        if (gender === "Female" && FEMALE_EXCLUDED_FIELD_IDS.has(question.fieldid)) {
            return false;
        }
        return true;
    })
    .map((question) => {
        if (question.fieldid !== "relationshipstatus1") {
            return { ...question, whatItMeans: QUESTION_MEANINGS[question.question] || question.whatItMeans };
        }
        const answers = gender === "Male"
            ? relationshipStatusOptionsByGender.male
            : relationshipStatusOptionsByGender.female;
        return { ...question, answers, whatItMeans: QUESTION_MEANINGS[question.question] || question.whatItMeans };
    })
);

const filterQuestionsForEditFlow = (questions) => (
    questions.filter((question) => (
        question?.fieldid
        && question?.showInEdit !== false
        && !["interlude", "image", "captcha"].includes(question?.type)
    ))
);

const filterQuestionsForNonWali = (questions) => (
    questions.filter((question) => (
        question?.fieldid
        && question.fieldid !== "walisrelationshiptoperson1"
    ))
);

const unneededbiodatawali = ["age1", "race1", "currentlocation1", "relationshipstatus1", "profession1", "tribe1"]

export const createProfileFields = profileQuestions;
export const waliSignupQuestions = profileQuestions.filter((question) => (
    (question.section === "Bio Details" && question.question !== "Relationship Status") ||
    question.fieldid === "termsaccepted1"
)).filter((question) => (
    !unneededbiodatawali.includes(question.fieldid) && !unneededbiodatawali.includes(question.fieldid2 ? question.fieldid2 : "n/a")
));

export const malequestions = applyQuestionRulesByGender(filterQuestionsForNonWali(createProfileFields), "Male");
export const femalequestions = applyQuestionRulesByGender(filterQuestionsForNonWali(createProfileFields), "Female");
export const maleEditQuestions = filterQuestionsForEditFlow(malequestions);
export const femaleEditQuestions = filterQuestionsForEditFlow(femalequestions);

export const generatorquestions = [
    {section: "Basic Information", question: "Age", type:"age", fieldid:'age1', answers:getMultiples(1,100)},
    {section: "Basic Information", question: "Country Of Residence", type:"choice", fieldid:'country1', answers:["Kenya","Tanzania","Uganda"]},
    {section: "Basic Information", question: "Current City", type:"text", fieldid:'city1', answers:[]},
    {section: "Basic Information", question: "Relationship Status", type:"choice", fieldid:'relationshipstatus1', answers:["Single", "Divorced", "Widowed"]},
    {section: "Basic Information", question: "Ethnic Background", type:"datalistinput", fieldid:'ethnicity1', answers:nationalitieslist},
]

export function makeid(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
}

export const DatalistInput = ({ id, options = [], placeholder, onChange }) => {
    const [value, setValue] = React.useState("");
    const normalizeOption = (option) => {
      if (option && typeof option === 'object') {
        return {
          value: option.value ?? option.label ?? '',
          label: option.label ?? option.value ?? '',
        };
      }
      return { value: option, label: option };
    };
    const normalizedOptions = options.map(normalizeOption);
  
    const handleChange = (event) => {
      const newValue = event.target.value;
      setValue(newValue);
      const matchedOption = normalizedOptions.find((option) => option.label === newValue || option.value === newValue);
      if (onChange) onChange(matchedOption ? matchedOption.value : newValue);
    };
  
    return (
      <div className="w-full">
        <input
          type="text"
          list={id}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          className="p-4 my-2 w-full text-lg border border-gray-300 rounded-md text-black bg-white placeholder-gray-600"
        />
        <datalist id={id}>
          {normalizedOptions.map((option, index) => (
            <option key={index} value={option.label} />
          ))}
        </datalist>
      </div>
    );
  };

export function getRandom(items) {
    var item = items[Math.floor(Math.random()*items.length)]; 
    return item
}

export function makeidnumber(length) {
    let result = '';
    const characters = '0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
}

export function fillerprofiledata(female) {
    const [searchParams, setSearchParams] = useSearchParams();
    const malelocation = searchParams.get("location")
    const randomPackage = getRandom(["Gold", "Platinum", "Diamond", "Emerald"]);
    const pass = makeid(34)
    const data = {
        EMAIL: female === "Female" ? "" : `${makeid(10)}@${makeid(10)}.com`,
        password:female === "Female" ? "" : pass,
        confirmpassword:female === "Female" ? "" : pass,
        phonenumber1:female === "Female" ? "" : makeidnumber(10),
        gender1:female || "Male",
        zodiacsign1: female === "Female" ? "" : getRandom(zodiacsigns),
        relationshippreferencereligion4: female === "Female" ? "" : "Christian",
        familybackground4: female === "Female" ? "" : "Irrelevant",
        religion1: female === "Female" ? "" : "Christian",
        package1: female === "Female" ? "" : randomPackage,
        relationshipstatus1: female === "Female" ? "" : "Divorced",
        country1: female === "Female" ? "" : malelocation
    }
    return data
}

export class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false };
    }
  
    componentDidCatch(error, errorInfo) {
      postData('/api/log-error', {
        type: `ReactErrorBoundary ${this.props.version}`,
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      }).catch((err) => {
        console.error("Error logging to server:", err);
        throw err
      });
      this.setState({ hasError: true });
    }
  
    render() {
      const gradientStyle = {
        // minHeight: '100%',
        // width: '100%',
        // backgroundImage:
        //   'linear-gradient(135deg, rgba(0,0,0,1) 20%, rgba(6,78,59,0.9) 55%, rgba(217,119,6,0.85) 100%)',
        // backgroundAttachment: 'fixed',
        // Wl1ith no animation, a huge background-size parks the view in the black corner.
        // Use full coverage and center the gradient so colors are visible.
        // Ensure the gradient fills the viewport on all aspect ratios
        // backgroundSize: 'cover',
        // backgroundPosition: 'center',
        // backgroundRepeat: 'no-repeat',
        // color: 'white',
      };

      const pathname = typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") : "";
      const useGradient = pathname !== "/privacypolicy" && pathname !== "/terms";
      const containerClass = `w-screen min-h-screen text-white ${useGradient ? "global-signup-gradient" : "bg-black"}`;

      if (this.state.hasError) {
        return (
          <div
            className={containerClass}
            style={gradientStyle}
          >
            <div className="flex items-center justify-center w-full h-full py-12">
              <h1 className="text-2xl font-semibold">Something went wrong.</h1>
            </div>
          </div>
        );
      }
      return (
        <div
          className={containerClass}
          style={gradientStyle}
        >
          {this.props.children}
        </div>
      );
    }
}

export function turntoacronym(str) {
    if (str) {
        // console.log("The thing acronymed:", str)
        var matches = str.match(/\b(\w)/g); // ['J','S','O','N']
        var acronym = matches.join(''); // JSON
        return acronym
    } else {
        return false
    }
    
}

export var Base64 = {

    // private property
    _keyStr : "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",

    // public method for encoding
    encode : function (input) {
        var output = "";
        var chr1, chr2, chr3, enc1, enc2, enc3, enc4;
        var i = 0;

        input = Base64._utf8_encode(input);

        while (i < input.length) {

            chr1 = input.charCodeAt(i++);
            chr2 = input.charCodeAt(i++);
            chr3 = input.charCodeAt(i++);

            enc1 = chr1 >> 2;
            enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
            enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
            enc4 = chr3 & 63;

            if (isNaN(chr2)) {
                enc3 = enc4 = 64;
            } else if (isNaN(chr3)) {
                enc4 = 64;
            }

            output = output +
            this._keyStr.charAt(enc1) + this._keyStr.charAt(enc2) +
            this._keyStr.charAt(enc3) + this._keyStr.charAt(enc4);
        }
        return output;
    },

    // public method for decoding
    decode : function (input) {
        var output = "";
        var chr1, chr2, chr3;
        var enc1, enc2, enc3, enc4;
        var i = 0;

        input = input.replace(/[^A-Za-z0-9\+\/\=]/g, "");

        while (i < input.length) {

            enc1 = this._keyStr.indexOf(input.charAt(i++));
            enc2 = this._keyStr.indexOf(input.charAt(i++));
            enc3 = this._keyStr.indexOf(input.charAt(i++));
            enc4 = this._keyStr.indexOf(input.charAt(i++));

            chr1 = (enc1 << 2) | (enc2 >> 4);
            chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
            chr3 = ((enc3 & 3) << 6) | enc4;

            output = output + String.fromCharCode(chr1);

            if (enc3 != 64) {
                output = output + String.fromCharCode(chr2);
            }
            if (enc4 != 64) {
                output = output + String.fromCharCode(chr3);
            }
        }

        output = Base64._utf8_decode(output);

        return output;
    },

    // private method for UTF-8 encoding
    _utf8_encode : function (string) {
        var string = string || ""
        var string = string.replace(/\r\n/g,"\n");
        var utftext = "";

        for (var n = 0; n < string.length; n++) {

            var c = string.charCodeAt(n);

            if (c < 128) {
                utftext += String.fromCharCode(c);
            }
            else if((c > 127) && (c < 2048)) {
                utftext += String.fromCharCode((c >> 6) | 192);
                utftext += String.fromCharCode((c & 63) | 128);
            }
            else {
                utftext += String.fromCharCode((c >> 12) | 224);
                utftext += String.fromCharCode(((c >> 6) & 63) | 128);
                utftext += String.fromCharCode((c & 63) | 128);
            }
        }
        return utftext;
    },

    // private method for UTF-8 decoding
    _utf8_decode(utftext) {
        var string = "";
        var i = 0;
        var c = 0, c1 = 0, c2 = 0, c3 = 0;
      
        while (i < utftext.length) {
          c = utftext.charCodeAt(i);
      
          if (c < 128) {
            string += String.fromCharCode(c);
            i++;
          } else if (c > 191 && c < 224) {
            c2 = utftext.charCodeAt(i + 1);
            string += String.fromCharCode(((c & 31) << 6) | (c2 & 63));
            i += 2;
          } else {
            c2 = utftext.charCodeAt(i + 1);
            c3 = utftext.charCodeAt(i + 2);
            string += String.fromCharCode(((c & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63));
            i += 3;
          }
        }
      
        return string;
      }
      
}

import PortraitPlaceholder from '../media/Portrait_Placeholder.png';

export function SmartImageComponent(props) {
    const [loading, setLoading] = React.useState(true)
    const [usebackup, setUseBackup] = React.useState(false)

    const cssclasses = props.cssclasses || ""
    const imageobjectcss = props.imageobjectcss || {}

    function testImage(URL) {
        var tester=new Image();
        tester.onload=imageFound;
        tester.onerror=imageNotFound;
        tester.src=URL;
    }
    
    function imageFound() {
        setLoading(false)
    }
    
    function imageNotFound() {
        console.log(props.src+' was not found.');
        if (props.backup) {
            setUseBackup(true)
            setLoading(false)
        }
    }
    
    React.useEffect(function() {
        testImage(props.src);
    }, [])

    if (!usebackup) {
        return <img 
                src={props.src} alt={props.id} 
                style={!loading ? imageobjectcss : {display: 'none'}} 
                onLoad={() => setLoading(false)} className={cssclasses}
                onClick={() => {
                    if (props.onClickFunction) props.onClickFunction()
                }}
                loading="lazy"
            />
    } else {
        const theurl = props.backupurl || PortraitPlaceholder
        return <img 
                src={theurl} alt={props.id} 
                style={!loading ? imageobjectcss : {display: 'none'}} 
                onLoad={() => setLoading(false)} className={cssclasses}
                onClick={() => {
                    if (props.onClickFunction) props.onClickFunction()
                }}
                loading="lazy"
            />
    }
}

export function shuffleArray(array) {
    for (var i = array.length - 1; i >= 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var temp = array[i];
        array[i] = array[j];
        array[j] = temp;
    }
}

export function timestamptodate(UNIX_timestamp) {
    // Create a new JavaScript Date object based on the timestamp
    // multiplied by 1000 so that the argument is in milliseconds, not seconds
    var date = new Date(UNIX_timestamp * 1000);

    // Hours part from the timestamp
    var hours = date.getHours();

    // Minutes part from the timestamp
    var minutes = "0" + date.getMinutes();

    // Seconds part from the timestamp
    var seconds = "0" + date.getSeconds();

    // Will display time in 10:30:23 format
    var formattedTime = hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);

    console.log(formattedTime);
    return formattedTime
}

export function calculateDaysHoursMinutes(startTimestamp, endTimestamp) {
    // Convert timestamps from seconds to milliseconds
    const msDifference = (endTimestamp - startTimestamp) * 1000;
  
    // Define time units in milliseconds
    const msPerDay = 24 * 60 * 60 * 1000;
    const msPerHour = 60 * 60 * 1000;
    const msPerMinute = 60 * 1000;
  
    // Calculate days, hours, and minutes
    const days = Math.floor(msDifference / msPerDay);
    const hours = Math.floor((msDifference % msPerDay) / msPerHour);
    const minutes = Math.floor((msDifference % msPerHour) / msPerMinute);
  
    return `${days} days ${hours} hrs ${minutes} mins`;
}

export function calculateDaysHoursMinutesdata(startTimestamp, endTimestamp) {
    // Convert timestamps from seconds to milliseconds
    const msDifference = (endTimestamp - startTimestamp) * 1000;
  
    // Define time units in milliseconds
    const msPerDay = 24 * 60 * 60 * 1000;
    const msPerHour = 60 * 60 * 1000;
    const msPerMinute = 60 * 1000;
  
    // Calculate days, hours, and minutes
    const days = Math.floor(msDifference / msPerDay);
    const hours = Math.floor((msDifference % msPerDay) / msPerHour);
    const minutes = Math.floor((msDifference % msPerHour) / msPerMinute);
  
    return {days, hours, minutes}
}
